import type { TicketBezugsKandidaten } from "@kundeninfo/api-client";
import type { AktenTabId } from "../KundenAkte.js";

/**
 * TEXTE DER BEZÜGE im Reiter „Kunden und Bezüge“ (Entscheid 499) — die Gruppentitel der Auswahl je Bezugsart und der
 * Reiter je Bezugsart für den Verweis (K6). Schlüssel englisch; die Beschriftung des Bezugs selbst liefert der Server.
 */
export type ReferenceKind = TicketBezugsKandidaten["items"][number]["kind"];

/** Der Reiter der Akte je Bezugsart (K6, PF20): Daraus wird der Bezug des Verweises (`bezugAusAkteZiel`) — Liefervertrag
 *  und Eingangsrechnung öffnen als Panel, Vorgänge springen bis Stufe 3 in ihren Reiter, eine Datei in die Dateien. */
export const REFERENCE_TAB = {
  OFFER: "angebote", SALES_ORDER: "auftraege", INVOICE: "rechnungen", FILE: "dateien",
  SUPPLIER_CONTRACT: "liefervertraege", INCOMING_INVOICE: "eingangsrechnungen",
} as const satisfies Record<ReferenceKind, AktenTabId>;

/** Die Art, mit der die Beschriftung des Servers beginnt („Liefervertrag Telekom Festnetz“, „Angebot BS-AN-… · Titel“) —
 *  der Verweis nennt die Art selbst („Zum Liefervertrag …“); eine Datei heißt nach ihrem Namen. */
export const REFERENCE_LABEL_KIND: Readonly<Record<ReferenceKind, string | null>> = {
  OFFER: "Angebot", SALES_ORDER: "Auftrag", INVOICE: "Rechnung", FILE: null, SUPPLIER_CONTRACT: "Liefervertrag",
  INCOMING_INVOICE: "Eingangsrechnung",
};

export const REFERENCE_TEXT = {
  // Die Rückfragen vor dem Lösen (Hausregel „Bestätigung vor Datenänderung“, 19.10.2026) — für Kunde, Ansprechpartner und
  // Bezug in EINER Form (Hausregel „durchgängig einheitliches Design“, 02.11.2026): klein, gestapelt, nicht rot (Rot-Regel:
  // `useBestaetigung`).
  unlinkCustomerTitle: "Kunde lösen?",
  unlinkContactTitle: "Ansprechpartner lösen?",
  unlinkReferenceTitle: "Bezug lösen?",
  unlinkText: (name: string): string => `${name} wird vom Ticket gelöst.`,
  unlink: "Lösen",
  groupTitle: {
    OFFER: "Angebote", SALES_ORDER: "Aufträge", INVOICE: "Rechnungen", FILE: "Dateien",
    SUPPLIER_CONTRACT: "Lieferverträge", INCOMING_INVOICE: "Eingangsrechnungen",
  } satisfies Record<ReferenceKind, string>,
  /** Der vorbelegte Bezug beim Anlegen aus Vertrag oder Eingangsrechnung (Aufgabe 22). */
  preset: "Bezug",
  /** Das Ticket ist angelegt, die Verknüpfung des Bezugs scheiterte (Fix-Runde 1, K1). */
  presetFailed: (label: string): string =>
    `Das Ticket ist angelegt, der Bezug „${label}“ ließ sich aber nicht verknüpfen. Er lässt sich im Ticket unter „Kunden und Bezüge“ nachtragen.`,
  openTicket: "Ticket öffnen",
  // Die Bezugswahl (Nachtrag Review 20, Aufgabe 22): Hinweis, Platzhalter und Leertexte nennen mit Lieferantensicht
  // (LEAD/ADMIN) alle sechs Arten, ohne sie die vier, die der Server anbietet.
  customerFirst: (supplier: boolean): string => `Kunde zuerst zuordnen — dann lassen sich seine ${supplier
    ? "Angebote, Aufträge, Rechnungen, Lieferverträge, Eingangsrechnungen und Dateien"
    : "Angebote, Aufträge, Rechnungen und Dateien"} als Bezug wählen.`,
  searchName: "Bezug suchen",
  searchPlaceholder: (supplier: boolean): string => (supplier
    ? "Angebot, Auftrag, Rechnung, Liefervertrag, Eingangsrechnung oder Datei" : "Angebot, Auftrag, Rechnung oder Datei"),
  searchHelp: "Suchen oder aus den letzten Vorgängen wählen",
  noMatches: "Keine passenden Vorgänge oder Dateien.",
  noDocuments: "Zu diesem Kunden gibt es keine Vorgänge oder Dateien.",
};

/** Alle Bezugsarten in der Reihenfolge der Gruppen — aus der Tabelle, die `satisfies Record<ReferenceKind, …>` vollständig hält. */
export const REFERENCE_KINDS = Object.keys(REFERENCE_TEXT.groupTitle) as readonly ReferenceKind[];
